import * as THREE from "three";
import { makePlushMaterial, plush } from "./plush";

/** Lego unit in world units: 1 stud = 1 unit horizontally; plate height ≈ 0.4 */
export const STUD = 1;
export const BRICK_H = 1.2; // 3 plates
export const STUD_R = 0.3;
export const STUD_H = 0.22;

const geoCache = new Map<string, THREE.BufferGeometry>();
const matCache = new Map<string, THREE.MeshStandardMaterial>();

function brickGeo(w: number, d: number, h: number): THREE.BufferGeometry {
  const key = `${w}x${d}x${h}`;
  let g = geoCache.get(key);
  if (g) return g;

  // Body + studs as one merged-ish group via BufferGeometryUtils would be nicer;
  // for speed we return a simple Box and add studs as child meshes in factory.
  g = new THREE.BoxGeometry(w * STUD, h, d * STUD);
  geoCache.set(key, g);
  return g;
}

function studGeo(): THREE.BufferGeometry {
  let g = geoCache.get("stud");
  if (!g) {
    g = new THREE.CylinderGeometry(STUD_R, STUD_R, STUD_H, 16);
    geoCache.set("stud", g);
  }
  return g;
}

function mat(color: number, emissive = false): THREE.MeshStandardMaterial {
  const key = `${color}|${emissive ? 1 : 0}`;
  let m = matCache.get(key);
  if (m) return m;
  m = plush.ready
    ? makePlushMaterial(color, emissive)
    : new THREE.MeshStandardMaterial({
        color,
        roughness: 0.35,
        metalness: 0.08,
        emissive: emissive ? new THREE.Color(color) : new THREE.Color(0x000000),
        emissiveIntensity: emissive ? 0.45 : 0,
      });
  matCache.set(key, m);
  return m;
}

export type BrickSize = { w: number; d: number; h?: number };

/**
 * Creates a studded brick mesh (body + studs). Pivot at brick center.
 */
export function createLegoBrick(
  size: BrickSize,
  color: number,
  opts: { emissive?: boolean; castShadow?: boolean } = {},
): THREE.Group {
  const h = size.h ?? BRICK_H;
  const group = new THREE.Group();
  group.name = `brick_${size.w}x${size.d}`;

  const body = new THREE.Mesh(brickGeo(size.w, size.d, h), mat(color, opts.emissive));
  body.castShadow = opts.castShadow ?? true;
  body.receiveShadow = true;
  group.add(body);

  const stud = studGeo();
  const studMat = mat(color, opts.emissive);
  const y = h / 2 + STUD_H / 2 - 0.01;
  for (let ix = 0; ix < size.w; ix++) {
    for (let iz = 0; iz < size.d; iz++) {
      const s = new THREE.Mesh(stud, studMat);
      s.position.set(
        (ix - (size.w - 1) / 2) * STUD,
        y,
        (iz - (size.d - 1) / 2) * STUD,
      );
      s.castShadow = opts.castShadow ?? true;
      group.add(s);
    }
  }

  // userData for gameplay
  group.userData.brick = { w: size.w, d: size.d, h, color };
  return group;
}


